import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PostalAddressExtension as PostalAddressModel } from 'tio-data/data/resources/postal-address.ext';
import type { PslfAnnualAutoSubmitAddress } from 'tio-data/data/resources/pslf-profile.type';

/**
 * The `postal_address_type` a person's own mailing address is kept under. The type alone does not
 * make a row theirs — servicer payment addresses use it too — so the owner is what identifies it.
 */
export const MAILING_ADDRESS_TYPE = 'MAILING';

/** The `contact_type` the PSLF form's correspondence address is kept under. */
export const PRIMARY_EMAIL_TYPE = 'PRIMARY';

function activePrimaryEmails(person: PersonModel): EmailAddressModel[] {
  return (person.emailAddresses ?? []).filter(
    (email) => email.contactType === PRIMARY_EMAIL_TYPE && email.isActive
  );
}

/**
 * The employee's correspondence email: the row `person.primaryEmailAddressId` names, which is the
 * record `Person#primary_email` reads and so the one the form prints. The server orders the
 * candidates (verified first, then lowest id) and the relationship's order carries none of that,
 * so nothing here re-derives it — a payload with several live rows and no server verdict resolves
 * to nothing rather than to a guess.
 */
export function primaryEmailFor(person: PersonModel): EmailAddressModel | undefined {
  const candidates = activePrimaryEmails(person);
  const canonicalId = person.primaryEmailAddressId;
  if (canonicalId) return candidates.find((email) => email.id === canonicalId);

  return candidates.length === 1 ? candidates[0] : undefined;
}

/**
 * True when the employee has a correspondence address this payload cannot pin down — the server
 * named a row the collection does not carry, or named none while several are live. Both leave
 * editing closed: a create would be refused as a second active row, and an update would land on
 * whichever row happened to arrive first.
 */
export function hasUnresolvedPrimaryEmail(person: PersonModel): boolean {
  if (primaryEmailFor(person)) return false;

  return !!person.primaryEmailAddressId || activePrimaryEmails(person).length > 0;
}

/**
 * The employee's mailing address. Read from `ownedPostalAddresses` — the polymorphic set the API
 * itself resolves the row from, and the one a newly created row joins without a refetch — with the
 * legacy through-User relationship behind it for a payload carrying no owner linkage. That fallback
 * re-checks the owner because the same relationship can carry financial-institution MAILING rows,
 * which are payment addresses. A deactivated row reads as absent, matching the active-only lookup
 * `PslfField` prefills the form from; showing one would report an address the form never prints,
 * and saving over it would leave the edit invisible. Saving creates a fresh active row instead,
 * which the one-active-row-per-person index allows alongside it.
 */
export function mailingAddressFor(person: PersonModel): PostalAddressModel | undefined {
  const isLiveMailing = (address: PostalAddressModel) =>
    address.postalAddressType === MAILING_ADDRESS_TYPE && address.isActive;
  // The linkage id, not the record: the owner of an institution's row is rarely in the payload,
  // and reading a sync belongsTo that is not loaded throws.
  const ownedByPerson = (address: PostalAddressModel) =>
    !!person.id && address.belongsTo('postalAddressable')?.id() === person.id;

  return (
    (person.ownedPostalAddresses ?? []).find(isLiveMailing) ??
    (person.postalAddresses ?? []).find(
      (address) => isLiveMailing(address) && ownedByPerson(address)
    )
  );
}

/** One-line rendering of a mailing address — `""` when there is nothing to show. */
export function formatPostalAddress(address?: PslfAnnualAutoSubmitAddress | null): string {
  if (!address) return '';
  const region = [address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [address.addressLine1, address.addressLine2, region].filter(Boolean).join(', ');
}
